import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Search, Wallet, Car } from "lucide-react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import TablePagination from "../components/TablePagination";
import { fmtDate } from "../lib/format";

export default function MaintenanceRecords() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/maintenance-records").then((r) => setRecords(r.data)); }, []);
  useEffect(() => { setPage(1); }, [search]);

  const filtered = records.filter((r) => {
    const q = search.toLowerCase();
    return !q || `${r.vehicle_reg} ${r.vehicle_model} ${r.jenis} ${r.bengkel} ${r.pegawai}`.toLowerCase().includes(q);
  });
  const paged = filtered.slice((page - 1) * perPage, page * perPage);
  const totalKos = records.reduce((s, r) => s + (Number(r.kos) || 0), 0);

  return (
    <div className="space-y-6 fade-up" data-testid="maintenance-records-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Wrench className="h-6 w-6 text-[#10056E]" /> Penyelenggaraan Kenderaan</h1>
        <p className="text-sm text-muted-foreground mt-1">Rekod penyelenggaraan berjadual mengikut Pekeliling WP 4.1 — {records.length} rekod.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-4 py-3 min-w-40">
          <Wrench className="h-4 w-4 text-[#10056E]" />
          <p className="text-2xl font-bold mt-1" data-testid="maintenance-total-count">{records.length}</p>
          <p className="text-xs text-muted-foreground">jumlah rekod</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-4 py-3 min-w-40">
          <Wallet className="h-4 w-4 text-amber-600" />
          <p className="text-2xl font-bold mt-1">RM {totalKos.toLocaleString("ms-MY", { minimumFractionDigits: 2 })}</p>
          <p className="text-xs text-muted-foreground">jumlah kos</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-4 py-3 min-w-40">
          <Car className="h-4 w-4 text-emerald-600" />
          <p className="text-2xl font-bold mt-1">{new Set(records.map((r) => r.vehicle_id)).size}</p>
          <p className="text-xs text-muted-foreground">kenderaan terlibat</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b flex justify-end">
          <div className="relative w-full sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Cari kenderaan, jenis, bengkel..." value={search}
              onChange={(e) => setSearch(e.target.value)} data-testid="maintenance-search-input" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/70">
                <TableHead>Tarikh Masuk</TableHead>
                <TableHead>Kenderaan</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead>Odometer (km)</TableHead>
                <TableHead>Bengkel</TableHead>
                <TableHead>Kos (RM)</TableHead>
                <TableHead>Pegawai</TableHead>
                <TableHead>Jangkaan Siap</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Tiada rekod penyelenggaraan.</TableCell></TableRow>
              )}
              {paged.map((r) => (
                <TableRow key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(r)} data-testid={`maintenance-row-${r.id}`}>
                  <TableCell className="text-sm">{r.tarikh_masuk ? fmtDate(r.tarikh_masuk) : "-"}</TableCell>
                  <TableCell className="text-sm">
                    <span className="font-semibold">{r.vehicle_reg}</span>
                    <span className="text-xs text-muted-foreground block">{r.vehicle_model} · {r.category}</span>
                  </TableCell>
                  <TableCell><Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200">{r.jenis || "-"}</Badge></TableCell>
                  <TableCell className="text-sm">{r.odometer ? Number(r.odometer).toLocaleString() : "-"}</TableCell>
                  <TableCell className="text-sm">{r.bengkel || "-"}</TableCell>
                  <TableCell className="text-sm font-semibold">{r.kos ? Number(r.kos).toFixed(2) : "-"}</TableCell>
                  <TableCell className="text-sm">{r.pegawai || "-"}</TableCell>
                  <TableCell className="text-sm">{r.jangkaan_siap ? fmtDate(r.jangkaan_siap) : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <TablePagination page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} total={filtered.length} />
      </div>

      {/* Maintenance detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Butiran Penyelenggaraan — {detail?.vehicle_reg}</DialogTitle>
            <DialogDescription className="text-xs">Rekod Penyelenggaraan Berjadual mengikut Pekeliling WP 4.1.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4" data-testid="maintenance-detail">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-bold">{detail.vehicle_reg}</p>
                  <p className="text-xs text-muted-foreground">{detail.vehicle_model} · {detail.category}</p>
                </div>
                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200">{detail.jenis || "-"}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Tarikh Masuk</p><p className="font-bold">{detail.tarikh_masuk ? fmtDate(detail.tarikh_masuk) : "-"}</p></div>
                <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Odometer</p><p className="font-bold">{detail.odometer ? `${Number(detail.odometer).toLocaleString()} km` : "-"}</p></div>
                <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Bengkel / Lokasi</p><p className="font-bold">{detail.bengkel || "-"}</p></div>
                <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Anggaran Kos</p><p className="font-bold">{detail.kos ? `RM ${Number(detail.kos).toFixed(2)}` : "-"}</p></div>
                <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Jangkaan Siap</p><p className="font-bold">{detail.jangkaan_siap ? fmtDate(detail.jangkaan_siap) : "-"}</p></div>
                <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Pegawai Mengesahkan</p><p className="font-bold">{detail.pegawai || "-"}</p></div>
              </div>
              <div className="border border-slate-200 bg-slate-50 rounded-lg p-3 text-sm">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1">Butir-butir penyelenggaraan & pemeriksaan berkala</p>
                <p>{detail.butiran || "-"}</p>
              </div>
              {(detail.jadual_tarikh || detail.jadual_odometer) && (
                <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 text-sm">
                  <p className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-1">Jadual penyelenggaraan seterusnya</p>
                  <p>{detail.jadual_tarikh ? fmtDate(detail.jadual_tarikh) : "-"}{detail.jadual_odometer ? ` · ${Number(detail.jadual_odometer).toLocaleString()} km` : ""}</p>
                </div>
              )}
              {detail.catatan && (
                <div className="border border-slate-200 bg-slate-50 rounded-lg p-3 text-sm">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1">Catatan</p>
                  <p>{detail.catatan}</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground">Direkod oleh {detail.recorded_by || "-"} · {fmtDate(detail.created_at)}</p>
              <Button variant="outline" className="w-full" onClick={() => navigate(`/kenderaan/${detail.vehicle_id}`)} data-testid="maintenance-view-vehicle-btn">
                <Car className="h-4 w-4 mr-2" /> Lihat Detail Kenderaan
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
